/**
 * Shared prop shapes for the `<CollectionPageSchema>` renderer.
 *
 * Kept separate from any single builder so both producers can depend on the
 * contract without depending on each other:
 *   - PL CLP  -> `server-side/page-data/pl/page-type-handlers/collection-page-schema-data-pl`
 *   - BFF guide/landing pages -> `server-side/page-data/landing/landing-page-schema-data`
 */

// Cap matches Schema.org / Google's recommendation to keep ItemList lightweight.
export const MAX_ITEM_LIST_PRODUCTS = 20

export interface CollectionPageSchemaProduct {
  name: string
  url: string
}

export interface CollectionPageSchemaBreadcrumb {
  name: string
  url?: string
}

/**
 * A subject the page is about. `sameAs` carries a canonical identifier so a consumer can
 * ground the name against a known entity; it is omitted whenever no confident mapping
 * exists, because a wrong identifier is worse than a bare name.
 */
export interface CollectionPageSchemaEntity {
  name: string
  sameAs?: string
}

export interface CollectionPageSchemaFaq {
  question: string
  answer: string
}

export interface CollectionPageSchemaProps {
  pageUrl: string
  pageName: string
  description: string
  /**
   * Fallback subject name, used when `aboutEntities` is empty. Still required: the PL CLP
   * builder supplies only this.
   */
  aboutName: string
  /**
   * Real subjects of the page, emitted as a `Thing[]`. When empty or absent the renderer
   * falls back to a single `Thing` built from `aboutName`, which is what keeps the PL CLP
   * output unchanged.
   */
  aboutEntities?: CollectionPageSchemaEntity[]
  numberOfItems: number
  products: CollectionPageSchemaProduct[]
  breadcrumbs: CollectionPageSchemaBreadcrumb[]
  /**
   * FAQ Q&A pairs. Empty array when FAQ data is absent — the renderer treats
   * empty-array and undefined the same way (no FAQPage / hasPart emitted).
   */
  faqs: CollectionPageSchemaFaq[]
  /**
   * Absolute URLs emitted as CollectionPage `significantLink`. Independent of
   * FAQ presence; the renderer emits the field only when the array is non-empty.
   */
  significantLinks: string[]
}
